import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import CustomTextField from "../../components/CustomTextField";
import { memo, useCallback, useMemo, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import {
  MEDIA_UPLOAD_CODES,
  errorAlertText,
  errorSubAlertText,
  uploadMedia,
} from "../../utils/photoUtils";
import { AccountData } from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import {
  INSTAGRAM_DEFAULT,
  LINKEDIN_DEFAULT,
  SPOTIFY_DEFAULT,
  TIKTOK_DEFAULT,
  X_DEFAULT,
  YOUTUBE_DEFAULT,
  censoredWordsDetected,
  checkUserHasSocials,
  detectedCensored,
} from "@markit/common.utils";
import AlertContainer from "../../components/Containers/AlertContainer";
import ProfilePic from "../ProfilePic";
import CustomTextFieldMultiline from "../TextFields/CustomTextFieldMultiline";
import RectangleButton from "../Buttons/RectangleButton";
import { useOnMount } from "../../hooks/useOnMount";
import TextHoverButton from "../Buttons/TextHoverButton";

export type EditProfileError = {
  type: "fullName" | "username" | "";
  message: string;
};

type EditProfileDetailsProps = {
  accountSettings: AccountData;
  updateAccountSettings: (accountSettings: Partial<AccountData>) => void;
  showError: EditProfileError;
  setShowError: (showError: EditProfileError) => void;
  loading: boolean;
  isOnboarding?: boolean;
};

const EditProfileDetails = (props: EditProfileDetailsProps) => {
  const {
    accountSettings,
    updateAccountSettings,
    showError,
    setShowError,
    loading,
    isOnboarding,
  } = props;
  const { accountData, teamMemberAccountData } =
    useSelector(getAccountState).account;
  const { theme } = useTheme();
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [showSocials, setShowSocials] = useState(false);
  const [isHoveringProfilePic, setIsHoveringProfilePic] = useState(false);
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);

  const styles = {
    labelText: { fontSize: 12, fontWeight: 500, color: Colors.BLACK },
    labelTagText: { fontSize: 12, fontWeight: 500, color: Colors.GRAY2 },
    labelTagTextError: {
      fontSize: 12,
      fontWeight: 500,
      color: theme.ErrorBorderColor.borderColor,
    },
  };

  useOnMount(() => {
    if (checkUserHasSocials(accountSettings)) {
      setShowSocials(true);
    }
  });

  const userDataToShow = useMemo(
    () => teamMemberAccountData || accountData,
    [accountData, teamMemberAccountData]
  );

  // Show full name input if not on onboarding or not in creator mode
  const showFullName = useMemo(
    () => !isOnboarding || !userDataToShow.inCreatorMode,
    [isOnboarding, userDataToShow.inCreatorMode]
  );

  // Show username input if not on onboarding or is in creator mode
  const showUsername = useMemo(
    () => !isOnboarding || userDataToShow.inCreatorMode,
    [isOnboarding, userDataToShow.inCreatorMode]
  );

  const updateAccountState = useCallback(
    (update: Partial<AccountData>) => {
      if (update.fullName !== undefined) {
        updateAccountSettings({ fullName: update.fullName });
        if (detectedCensored(update.fullName)) {
          setShowError({
            type: "fullName",
            message: `Content Violation: Banned words detected (${censoredWordsDetected(
              update.fullName
            )})`,
          });
          return;
        } else {
          setShowError({ type: "", message: "" });
        }
      }
      if (update.username !== undefined) {
        setShowError({ type: "", message: "" });
        updateAccountSettings({ username: update.username });
      }
    },
    [setShowError, updateAccountSettings]
  );

  const removeProfilePic = useCallback(() => {
    updateAccountSettings({ profilePicURL: "" });
  }, [updateAccountSettings]);

  const uploadImage = useCallback(
    (file: File) => {
      return new Promise<void>((resolve, reject) => {
        (async () => {
          setIsUploadingProfilePic(true);
          const uploadData = await uploadMedia(file, true, true);
          if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
            setAlertText({
              heading: errorAlertText(uploadData.uploadCode, true),
              subHeading: errorSubAlertText(uploadData.uploadCode),
            });

            setIsUploadingProfilePic(false);
            reject();
          } else {
            updateAccountSettings({ profilePicURL: uploadData.blobUrl });
            setShowError({ type: "", message: "" });
            setIsUploadingProfilePic(false);
            resolve();
          }
        })().catch((error) => {
          setIsUploadingProfilePic(false);
          reject(error);
        });
      });
    },
    [setShowError, updateAccountSettings]
  );

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 14 }}>
        <div
          className="ColumnCentering onboarding-profile-pic-photo-target"
          style={{ gap: 7 }}
        >
          <div className="AlignedRow" style={{ gap: 7 }}>
            <span style={styles.labelText}>
              {isOnboarding ? "Add a " : ""}Profile Photo
            </span>
            {accountSettings.profilePicURL ? (
              <TextHoverButton
                text="Remove"
                onPress={removeProfilePic}
                textStyles={{ color: Colors.RED3, fontWeight: 500 }}
              />
            ) : (
              <span style={styles.labelTagText}>{"(optional)"}</span>
            )}
          </div>
          <div
            style={{
              position: "relative",
              width: 80,
              height: 80,
              overflow: "hidden",
              borderRadius: 80 / 2,
            }}
          >
            <FileUploader
              handleChange={uploadImage}
              name="file"
              types={["PNG", "JPG", "JPEG"]}
              onTypeError={() =>
                setAlertText({
                  heading: errorAlertText(MEDIA_UPLOAD_CODES.INVALID, true),
                  subHeading: errorSubAlertText(MEDIA_UPLOAD_CODES.INVALID),
                })
              }
              dropMessageStyle={{
                borderRadius: 80 / 2,
                border: "none",
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                opacity: 1,
              }}
              hoverTitle=" "
              disabled={isUploadingProfilePic}
            >
              <div
                className="Centering"
                onMouseEnter={() => setIsHoveringProfilePic(true)}
                onMouseLeave={() => setIsHoveringProfilePic(false)}
                style={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  borderRadius: 80 / 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  border: `0.5px solid ${Colors.GRAY2}`,
                }}
              >
                <ProfilePic
                  user={accountSettings}
                  size={80}
                  forcePointer
                  blueGradientBackground
                />
                {isUploadingProfilePic ? (
                  <div
                    className="Centering"
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 80 / 2,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                    }}
                  >
                    <CircularProgress
                      style={{ color: Colors.BLACK }}
                      size={24}
                    />
                  </div>
                ) : isHoveringProfilePic ? (
                  <div
                    className="Centering"
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 80 / 2,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      cursor: "pointer",
                    }}
                  >
                    <Icon
                      icon="ion:create"
                      height={24}
                      style={{ color: Colors.BLACK }}
                    />
                  </div>
                ) : null}
              </div>
            </FileUploader>
          </div>
        </div>
        {showFullName ? (
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <span style={styles.labelText}>
                {userDataToShow.inCreatorMode ? "Account" : "Full"} Name
              </span>
              <span
                style={
                  showError.type === "fullName"
                    ? styles.labelTagTextError
                    : styles.labelTagText
                }
              >
                {"(required)"}
              </span>
            </div>
            <CustomTextField
              value={accountSettings.fullName}
              placeholder="Name"
              disabled={loading}
              borderRadius={12}
              inputMode="text"
              error={showError.type === "fullName"}
              onChange={(change: any) => {
                updateAccountState({ fullName: change.target.value });
              }}
              altPadding={"8px 7px"}
              startAdornment={
                <Icon
                  icon={"ion:person"}
                  height={22}
                  style={{
                    color:
                      showError.type === "fullName"
                        ? Colors.RED3
                        : accountSettings.fullName
                          ? Colors.BLACK
                          : Colors.GRAY2,
                    marginLeft: 14,
                  }}
                />
              }
              maxLength={42}
            />
            {showError.type === "fullName" ? (
              <span style={{ fontSize: 12, color: Colors.RED2 }}>
                {showError.message}
              </span>
            ) : null}
          </div>
        ) : null}
        {showUsername ? (
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <span style={styles.labelText}>Username</span>
              <span
                style={
                  showError.type === "username"
                    ? styles.labelTagTextError
                    : styles.labelTagText
                }
              >
                {"(required)"}
              </span>
            </div>
            <CustomTextField
              value={accountSettings.username}
              placeholder="Username"
              disabled={loading}
              borderRadius={12}
              inputMode="text"
              error={showError.type === "username"}
              onChange={(change: any) => {
                updateAccountState({ username: change.target.value.trim() });
              }}
              altPadding={"8px 7px"}
              noAutoCapitalize
              startAdornment={
                <Icon
                  icon={"ion:at"}
                  height={22}
                  style={{
                    color: accountSettings.username
                      ? Colors.BLACK
                      : Colors.GRAY2,
                    marginLeft: 14,
                  }}
                />
              }
              maxLength={30}
            />
            {showError.type === "username" ? (
              <span style={{ fontSize: 12, color: Colors.RED2 }}>
                {showError.message}
              </span>
            ) : accountSettings.username.toLowerCase() !==
                userDataToShow.username.toLowerCase() && !isOnboarding ? (
              <span style={{ fontSize: 12, color: Colors.RED2 }}>
                Warning: Username change will alter your profile URL
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="ColumnNormal" style={{ gap: 14 }}>
        {userDataToShow.inCreatorMode || !isOnboarding ? (
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <span style={styles.labelText}>Bio</span>
              <span style={styles.labelTagText}>{"(optional)"}</span>
            </div>
            <CustomTextFieldMultiline
              value={accountSettings.bio}
              placeholder="Bio"
              disabled={loading}
              onChange={(change: any) => {
                updateAccountSettings({ bio: change.target.value });
              }}
              containerStyles={{ backgroundColor: Colors.WHITE }}
              maxLength={500}
            />
            <span className="bodySmallRegular AlignedRowRight">
              {accountSettings.bio.length}/500
            </span>
          </div>
        ) : null}
        <div className="ColumnNormal" style={{ gap: 7 }}>
          <div className="AlignedRow" style={{ gap: 7 }}>
            <span style={styles.labelText}>Social Links</span>
            <span style={styles.labelTagText}>{"(optional)"}</span>
          </div>
          {showSocials ? (
            <div>
              <CustomTextField
                value={accountSettings.instagram}
                placeholder={INSTAGRAM_DEFAULT}
                borderRadius={12}
                rectangleBottom
                noBottomBorder
                inputMode="text"
                onChange={(change: any) => {
                  updateAccountSettings({ instagram: change.target.value });
                }}
                startAdornment={
                  <Icon
                    icon="mdi:instagram"
                    height={18}
                    style={{ color: Colors.BLACK, marginLeft: 14 }}
                  />
                }
              />
              <CustomTextField
                value={accountSettings.linkedin}
                placeholder={LINKEDIN_DEFAULT}
                borderRadius={12}
                rectangleTop
                rectangleBottom
                noBottomBorder
                inputMode="text"
                onChange={(change: any) => {
                  updateAccountSettings({ linkedin: change.target.value });
                }}
                startAdornment={
                  <Icon
                    icon="ri:linkedin-fill"
                    height={18}
                    style={{ color: Colors.BLACK, marginLeft: 14 }}
                  />
                }
              />
              <CustomTextField
                value={accountSettings.twitter}
                placeholder={X_DEFAULT}
                borderRadius={12}
                rectangleTop
                rectangleBottom
                noBottomBorder
                inputMode="text"
                onChange={(change: any) => {
                  updateAccountSettings({ twitter: change.target.value });
                }}
                startAdornment={
                  <Icon
                    icon="ri:twitter-x-fill"
                    height={18}
                    style={{ color: Colors.BLACK, marginLeft: 14 }}
                  />
                }
              />
              <CustomTextField
                value={accountSettings.tiktok}
                placeholder={TIKTOK_DEFAULT}
                inputMode="text"
                borderRadius={12}
                rectangleTop
                rectangleBottom
                noBottomBorder
                onChange={(change: any) => {
                  updateAccountSettings({ tiktok: change.target.value });
                }}
                startAdornment={
                  <Icon
                    icon="ic:baseline-tiktok"
                    height={18}
                    style={{ color: Colors.BLACK, marginLeft: 14 }}
                  />
                }
              />
              <CustomTextField
                value={accountSettings.spotify}
                placeholder={SPOTIFY_DEFAULT}
                inputMode="text"
                borderRadius={12}
                rectangleTop
                rectangleBottom
                noBottomBorder
                onChange={(change: any) => {
                  updateAccountSettings({ spotify: change.target.value });
                }}
                startAdornment={
                  <Icon
                    icon="mdi:spotify"
                    height={18}
                    style={{ color: Colors.BLACK, marginLeft: 14 }}
                  />
                }
              />
              <CustomTextField
                value={accountSettings.youtube}
                placeholder={YOUTUBE_DEFAULT}
                inputMode="text"
                borderRadius={12}
                rectangleTop
                onChange={(change: any) => {
                  updateAccountSettings({ youtube: change.target.value });
                }}
                startAdornment={
                  <Icon
                    icon="ion:logo-youtube"
                    height={18}
                    style={{ color: Colors.BLACK, marginLeft: 14 }}
                  />
                }
              />
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              <RectangleButton
                buttonLabel="Add Links"
                iconRight={
                  <Icon
                    icon="ion:add-circle"
                    height={14}
                    style={{ color: Colors.BLACK }}
                  />
                }
                onPress={() => setShowSocials(true)}
                altColor={Colors.WHITE1}
                altTextColor={Colors.BLACK}
                containerStyles={{
                  padding: "14px 10px",
                  border: `0.5px solid ${Colors.GRAY2}`,
                }}
              />
            </div>
          )}
        </div>
        </div>
      </div>
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <Icon icon="mdi-image-remove" height={40} />
            <span style={{ fontSize: 16, fontWeight: "500" }}>
              {alertText.heading}
            </span>
          </div>
        }
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        theme={theme}
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </>
  );
};

export default memo(EditProfileDetails);
